import type { ApiResponse } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private clearAuth(): void {
    // localStorageの認証情報をすべて削除
    localStorage.removeItem('token');
    localStorage.removeItem('auth-storage');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const token = this.getToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle 401 Unauthorized - トークン期限切れ
        if (response.status === 401 && !endpoint.includes('/login')) {
          this.clearAuth();
          // 現在のページがログインページでなければリダイレクト
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return data as ApiResponse<T>;
      }

      return data as ApiResponse<T>;
    } catch (error) {
      console.error('API request error:', error);
      return {
        success: false,
        error: 'ネットワークエラーが発生しました',
      };
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient();

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ token: string; user: import('@/types').User }>('/api/auth/login', { email, password }),
  
  me: () => api.get<import('@/types').User>('/api/auth/me'),
  
  getUsers: () => api.get<import('@/types').User[]>('/api/auth/users'),
  
  createUser: (data: import('@/types').UserForm) =>
    api.post<import('@/types').User>('/api/auth/users', data),
  
  updateUser: (id: number, data: Partial<import('@/types').UserForm & { is_active: boolean }>) =>
    api.put<import('@/types').User>(`/api/auth/users/${id}`, data),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.put('/api/auth/password', { current_password: currentPassword, new_password: newPassword }),
  
  setupStatus: () => api.get<{ initialized: boolean; has_jwt_secret: boolean }>('/api/auth/setup/status'),
  
  setup: (data: import('@/types').UserForm) =>
    api.post<{ user: import('@/types').User; suggested_jwt_secret: string }>('/api/auth/setup', data),
};

// Products API
export const productsApi = {
  list: (params?: { search?: string; category_id?: number; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.category_id) searchParams.set('category_id', params.category_id.toString());
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return api.get<import('@/types').PaginatedResponse<import('@/types').Product>>(
      `/api/products${query ? `?${query}` : ''}`
    );
  },
  
  get: (id: number) => api.get<import('@/types').Product & { inventory: import('@/types').Inventory[] }>(`/api/products/${id}`),
  
  create: (data: import('@/types').ProductForm) => api.post<import('@/types').Product>('/api/products', data),
  
  update: (id: number, data: Partial<import('@/types').ProductForm & { is_active: boolean }>) =>
    api.put<import('@/types').Product>(`/api/products/${id}`, data),
  
  delete: (id: number) => api.delete(`/api/products/${id}`),
};

// Categories API
export const categoriesApi = {
  list: () => api.get<import('@/types').Category[]>('/api/categories'),
  
  get: (id: number) => api.get<import('@/types').Category>(`/api/categories/${id}`),
  
  create: (data: { name: string; description?: string; sort_order?: number }) =>
    api.post<import('@/types').Category>('/api/categories', data),
  
  update: (id: number, data: { name?: string; description?: string; sort_order?: number }) =>
    api.put<import('@/types').Category>(`/api/categories/${id}`, data),
  
  delete: (id: number) => api.delete(`/api/categories/${id}`),
};

// Inventory API
export const inventoryApi = {
  list: (params?: {
    product_id?: number;
    show_zero?: boolean;
    expiring_only?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.product_id) searchParams.set('product_id', params.product_id.toString());
    if (params?.show_zero) searchParams.set('show_zero', 'true');
    if (params?.expiring_only) searchParams.set('expiring_only', 'true');
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return api.get<import('@/types').PaginatedResponse<import('@/types').Inventory>>(
      `/api/inventory${query ? `?${query}` : ''}`
    );
  },
  
  summary: () => api.get<import('@/types').InventorySummary[]>('/api/inventory/summary'),
  
  fifo: (productId: number) => api.get<import('@/types').Inventory[]>(`/api/inventory/fifo/${productId}`),
  
  stockIn: (data: import('@/types').StockInForm) =>
    api.post<import('@/types').Inventory>('/api/inventory/in', data),
  
  stockOut: (data: import('@/types').StockOutForm) =>
    api.post<import('@/types').Inventory>('/api/inventory/out', data),
  
  adjust: (data: import('@/types').StockAdjustForm) =>
    api.post<import('@/types').Inventory>('/api/inventory/adjust', data),
  
  expiring: (days?: number) =>
    api.get<import('@/types').Inventory[]>(`/api/inventory/expiring${days ? `?days=${days}` : ''}`),
  
  lowStock: () =>
    api.get<Array<{ id: number; name: string; unit: string; min_stock: number; total_stock: number }>>(
      '/api/inventory/low-stock'
    ),
};

// Transactions API
export const transactionsApi = {
  list: (params?: {
    product_id?: number;
    inventory_id?: number;
    type?: 'in' | 'out' | 'adjust';
    user_id?: number;
    start_date?: string;
    end_date?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.product_id) searchParams.set('product_id', params.product_id.toString());
    if (params?.inventory_id) searchParams.set('inventory_id', params.inventory_id.toString());
    if (params?.type) searchParams.set('type', params.type);
    if (params?.user_id) searchParams.set('user_id', params.user_id.toString());
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return api.get<import('@/types').PaginatedResponse<import('@/types').Transaction>>(
      `/api/transactions${query ? `?${query}` : ''}`
    );
  },
  
  get: (id: number) => api.get<import('@/types').Transaction>(`/api/transactions/${id}`),
  
  recent: (limit?: number) =>
    api.get<import('@/types').Transaction[]>(`/api/transactions/recent${limit ? `?limit=${limit}` : ''}`),
  
  summary: (params?: { start_date?: string; end_date?: string; group_by?: 'day' | 'week' | 'month' }) => {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    if (params?.group_by) searchParams.set('group_by', params.group_by);
    const query = searchParams.toString();
    return api.get<Array<{
      period: string;
      total_in: number;
      total_out: number;
      total_adjust: number;
      transaction_count: number;
    }>>(`/api/transactions/summary${query ? `?${query}` : ''}`);
  },
};

// Dashboard API
export const dashboardApi = {
  get: () => api.get<import('@/types').DashboardData>('/api/dashboard'),
  
  alerts: () => api.get<import('@/types').Alert[]>('/api/dashboard/alerts'),
};
